import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { AddonReturn } from "@/types/facility/addon-returns";
import { getColumns } from "./columns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Edit2, X } from "lucide-react";
import { displayDate, convert24HourTo12Hour } from "@/lib/helper";

interface AddonReturnsTableProps {
  data: AddonReturn[];
  isLoading: boolean;
  editingRowId: number | null;
  editingQuantity: number;
  setEditingRowId: (id: number | null) => void;
  setEditingQuantity: (qty: number) => void;
  handleSave: (bookingId: number, addonId: number, quantity: number) => Promise<void>;
  searchCriteria: { sort_key: string; sort_order: string };
  toggleSortOrder: (key: string, currentOrder: string) => void;
}

export default function AddonReturnsTable({
  data,
  isLoading,
  editingRowId,
  editingQuantity,
  setEditingRowId,
  setEditingQuantity,
  handleSave,
  searchCriteria,
  toggleSortOrder,
}: AddonReturnsTableProps) {
  const columns = React.useMemo(
    () => getColumns(
      editingRowId,
      editingQuantity,
      setEditingRowId,
      setEditingQuantity,
      handleSave,
      searchCriteria,
      toggleSortOrder
    ),
    [editingRowId, editingQuantity, setEditingRowId, setEditingQuantity, handleSave, searchCriteria, toggleSortOrder]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const addonReturnsPermission = (() => {
    try {
      const accessLevels = JSON.parse(localStorage.getItem("accessLevels") as string);
      return accessLevels.addon_returns ?? "no_access";
    } catch {
      return "no_access";
    }
  })();

  return (
    <div>
      {/* ── Desktop table (md and up) ── */}
      <div className="hidden md:block rounded-none border border-border bg-white">
        <ScrollArea className="w-full">
          <Table>
            <TableHeader className="bg-secondary">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="whitespace-nowrap">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    <div className="flex justify-center items-center space-x-2">
                      <div className="size-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-2 bg-primary rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* ── Mobile card list (below md) ── */}
      <div className="block md:hidden space-y-3">
        {isLoading ? (
          <div className="flex justify-center items-center space-x-2 py-10">
            <div className="size-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-2 bg-primary rounded-full animate-bounce"></div>
          </div>
        ) : data.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-10">No results found.</p>
        ) : (
          data.map((item) => {
            const isEditing = editingRowId === item.id;
            return (
              <div
                key={item.id}
                className="bg-white border border-border rounded-lg p-4 space-y-2 shadow-sm"
              >
                {/* Header: name + actions */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-sm">{item.name || "—"}</p>
                    <span className="text-xs text-gray-500 capitalize">{item.item_type || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isEditing ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleSave(item.booking_id, item.add_on_id, editingQuantity)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setEditingRowId(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      addonReturnsPermission === "full_access" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            setEditingRowId(item.id);
                            setEditingQuantity(item.returned_quantity);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      )
                    )}
                  </div>
                </div>

                {/* Detail grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                  <div>
                    <span className="font-medium text-gray-700">Booking Date: </span>
                    {displayDate(item.booking_date)}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Start: </span>
                    {convert24HourTo12Hour(item.start_time)}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">End: </span>
                    {convert24HourTo12Hour(item.end_time)}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Issued Qty: </span>
                    {item.max_attendees}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Returned Qty: </span>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editingQuantity}
                        onChange={(e) => setEditingQuantity(Number(e.target.value))}
                        className="w-20 h-7 text-xs mt-0.5"
                        autoFocus
                      />
                    ) : (
                      item.returned_quantity
                    )}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Remaining Qty: </span>
                    {item.remaining_quantity}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
